"use client";

import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { EstateOffer, PromoCodeInfo, UserInfo } from "@/types";
import {
  fetchLatest,
  getUserInfo,
  login,
  payment,
  paymentVerification,
  register,
  resetPasswordRequest,
  unsubscribeEmail,
  verify,
} from "@/utils";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type SessionContextType = {
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  showPasswordRepeat: boolean;
  setShowPasswordRepeat: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  isPendingRegistration: boolean;
  doPayment: boolean;
  setDoPayment: React.Dispatch<React.SetStateAction<boolean>>;
  latestEstateOffers: EstateOffer[];
  handleSubmitPayment: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  selectedProduct: string;
  setSelectedProduct: React.Dispatch<React.SetStateAction<string>>;
  registrationPromoCode: string;
  setRegistrationPromoCode: React.Dispatch<React.SetStateAction<string>>;
  registrationPromoInfo: PromoCodeInfo | null;
  setRegistrationPromoInfo: React.Dispatch<
    React.SetStateAction<PromoCodeInfo | null>
  >;
  isPromoCodeChecking: boolean;
  setIsPromoCodeChecking: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  passwordMatch: boolean;
  doForgottenPassword: boolean;
  setDoForgottenPassword: React.Dispatch<React.SetStateAction<boolean>>;
  isInitialLoading: boolean;
  isPaymentLoading: boolean;
  isPaymentSuccess: boolean;
  setIsInitialLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPaymentSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  onLoginSuccess: (userInfo: UserInfo, doReg: boolean) => Promise<void>;
  loginSignal: number;
};

const defaultContext: SessionContextType = {
  showPassword: false,
  setShowPassword: () => {},
  showPasswordRepeat: false,
  setShowPasswordRepeat: () => {},
  isLoading: false,
  isPendingRegistration: false,
  doPayment: false,
  setDoPayment: () => {},
  latestEstateOffers: [],
  handleSubmitPayment: async () => {},
  selectedProduct: "",
  setSelectedProduct: () => {},
  registrationPromoCode: "",
  setRegistrationPromoCode: () => {},
  registrationPromoInfo: null,
  setRegistrationPromoInfo: () => {},
  isPromoCodeChecking: false,
  setIsPromoCodeChecking: () => {},
  handleSubmit: async () => {},
  passwordMatch: true,
  doForgottenPassword: false,
  setDoForgottenPassword: () => {},
  isInitialLoading: true,
  isPaymentSuccess: false,
  isPaymentLoading: false,
  setIsInitialLoading: () => {},
  setIsPaymentSuccess: () => {},
  onLoginSuccess: async () => {},
  loginSignal: 0,
};

const SessionContext = createContext<SessionContextType>(defaultContext);

function clearLoginHash() {
  if (window.location.hash !== "#prihlaseni") return;

  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

// CAN NOT IMPORT watchdog context
export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const {
    doLogin,
    setDoLogin,
    userInfoGlobal,
    setUserInfoGlobal,
    setIsLoggedIn,
    setErrorMessage,
  } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      passwordsDoNotMatch: "Hesla se neshodují!",
    },
    en: {
      passwordsDoNotMatch: "Passwords do not match!",
    },
  }[language];

  const [selectedProduct, setSelectedProduct] = useState(
    defaultContext.selectedProduct,
  );
  const [registrationPromoCode, setRegistrationPromoCode] = useState(
    defaultContext.registrationPromoCode,
  );
  const [registrationPromoInfo, setRegistrationPromoInfo] =
    useState<PromoCodeInfo | null>(defaultContext.registrationPromoInfo);
  const [isPromoCodeChecking, setIsPromoCodeChecking] = useState(false);
  const [showPassword, setShowPassword] = useState(defaultContext.showPassword);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(
    defaultContext.showPasswordRepeat,
  );
  const [isLoading, setIsLoading] = useState(defaultContext.isLoading);
  const [isPendingRegistration, setIsPendingRegistration] = useState(
    defaultContext.isPendingRegistration,
  );
  const [doPayment, setDoPayment] = useState(defaultContext.doPayment);
  const [latestEstateOffers, setLatestEstateOffers] = useState<EstateOffer[]>(
    [],
  );
  const [passwordMatch, setPasswordMatch] = useState(
    defaultContext.passwordMatch,
  );
  const [doForgottenPassword, setDoForgottenPassword] = useState(
    defaultContext.doForgottenPassword,
  );
  const [isInitialLoading, setIsInitialLoading] = useState(
    defaultContext.isInitialLoading,
  );
  const [isPaymentLoading, setIsPaymentLoading] = useState(
    defaultContext.isPaymentLoading,
  );
  const [loginSignal, setLoginSignal] = useState<number>(0);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(
    defaultContext.isPaymentSuccess,
  );
  const didInitRef = useRef(false);

  const router = useRouter();

  const clearSession = async () => {
    localStorage.removeItem("userInfo");
    setUserInfoGlobal(null);
    setIsLoggedIn(false);

    try {
      await fetch("/api/logout", {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-api-token": "hjkads5io5",
        },
      });
    } catch (err) {
      setErrorMessage(String(err));
    }
  };

  const getLatest = async () => {
    try {
      if (!userInfoGlobal?.token) return;

      const payload: Record<string, any> = {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
      };
      const response = await fetchLatest(payload, userInfoGlobal.token);

      if (response.status === 200) {
        setLatestEstateOffers(response.data.data);
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const payload = {
      token: userInfoGlobal!.token,
      ...Object.fromEntries(new FormData(e.currentTarget).entries()),
    } as Record<string, any>;

    try {
      const response = await payment(payload);
      if (response.status === 200) {
        if (response.data.data) {
          window._gopay.checkout({
            gatewayUrl: response.data.data.gw_url,
            inline: true,
          });
          return;
        } else {
          await doLoginOk();
        }
      } else {
        localStorage.removeItem("userInfo");
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const form = e.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries()) as Record<
      string,
      any
    >;

    if (doForgottenPassword) {
      try {
        await resetPasswordRequest(payload);
      } catch (err) {
        setErrorMessage(String(err));
      } finally {
        form.reset();
        setIsLoading(false);
      }
      return;
    }

    if (!doLogin && payload["password-repeat"] !== payload.password) {
      setPasswordMatch(false);
      setErrorMessage("");
      toast.error(copy.passwordsDoNotMatch);
      setIsLoading(false);
      return;
    }
    setPasswordMatch(true);
    delete payload["password-repeat"];
    payload.doRegister = !doLogin;

    try {
      const response = doLogin ? await login(payload) : await register(payload);
      if (response.status === 200) {
        await onLoginSuccess(response.data.data, !doLogin);
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const onLoginSuccess = async (userInfo: UserInfo, doReg: boolean) => {
    const userData = { ...userInfo };
    if (!userData.email || !userData.token) {
      return;
    }

    await fetch("/api/login", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-api-token": "hjkads5io5",
      },
      body: JSON.stringify({ email: userData.email, token: userData.token }),
    });

    localStorage.setItem(
      "userInfo",
      JSON.stringify({
        email: userData.email,
        token: userData.token,
      }),
    );
    setIsLoggedIn(!doReg);
    setIsPendingRegistration(doReg);
    setUserInfoGlobal(userData);
    setLoginSignal((prev) => prev + 1);

    clearLoginHash();
  };

  // switch to register/payment flow
  useEffect(() => {
    if (selectedProduct) {
      setDoPayment(
        selectedProduct !== "BASIC" &&
          !(registrationPromoInfo?.valid && registrationPromoInfo.free),
      );
      setDoLogin(false);
    }
  }, [registrationPromoInfo, selectedProduct]);

  async function doLoginOk() {
    const response2 = await getUserInfo(userInfoGlobal!);
    const userInfo = {
      ...response2.data.data,
      token: userInfoGlobal!.token,
    };
    setUserInfoGlobal(userInfo);
    await onLoginSuccess(userInfo, false);
  }

  const checkPayment = async (userData?: UserInfo) => {
    setIsPaymentLoading(true);
    const url = new URL(window.location.href);
    const isReg = url.searchParams.has("registrace-platba");
    const isChange = url.searchParams.has("zmena-clenstvi-platba");
    const idParam = url.searchParams.get("id");
    const idParent = url.searchParams.get("parent_id");

    if (isReg && idParam) {
      const idNumber = parseInt(idParam, 10);
      if (!isNaN(idNumber)) {
        const response = await paymentVerification({
          id: String(idNumber),
          isRegistration: true,
        });
        if (response.status === 200) {
          setIsPaymentSuccess(true);
          setUserInfoGlobal(userData!);
        }
        router.push("/");
      }
    }

    if (isChange && idParam) {
      const idNumber = parseInt(idParam, 10);
      if (!isNaN(idNumber)) {
        const response = await paymentVerification({
          id: String(idNumber),
          isRegistration: false,
        });
        if (response.status === 200) {
          setIsPaymentSuccess(true);
        }
        router.push("/");
      }
    }

    if (idParent && idParam) {
      const idNumber = parseInt(idParam, 10);
      if (!isNaN(idNumber)) {
        const response = await paymentVerification({
          id: String(idNumber),
          parentId: String(idParent),
          isRegistration: false,
        });

        if (response.status === 202) {
          if (userData == null) {
            const payload = {
              email: userInfoGlobal?.email,
              token: "",
            };
            const response2 = await getUserInfo(payload);
            await onLoginSuccess(response2.data.data, false);
          }
          router.push("/");
        } else {
          router.push("/");
        }
      }
    }

    setIsPaymentLoading(false);
  };

  useEffect(() => {
    if (!isPaymentSuccess) return;
    const run = async () => {
      if (userInfoGlobal) {
        await doLoginOk();
      }
      setIsPaymentSuccess(false);
    };

    run();
  }, [isPaymentSuccess, onLoginSuccess]);

  const verifyToken = async (userInfo: UserInfo): Promise<boolean> => {
    if (!userInfo?.token || !userInfo?.email) return false;

    const payload = {
      email: userInfo.email,
      token: userInfo.token,
    };

    try {
      const response = await verify(payload);

      if (response.status === 200) {
        const response2 = await getUserInfo(payload);

        if (response2.status === 200 && response2.data?.data) {
          setUserInfoGlobal({
            ...response2.data.data,
            token: payload.token,
          });
          return true; // verified successfully
        } else {
          return false; // failed to get user info
        }
      } else if (response.status === 202) {
        // registration in progress, treat as not verified
        return false;
      } else {
        return false; // any other status = not verified
      }
    } catch (err) {
      setErrorMessage(String(err));
      return false;
    }
  };

  useEffect(() => {
    if (!isPendingRegistration) {
      getLatest();
    }
  }, [userInfoGlobal]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const init = async () => {
      const raw = localStorage.getItem("userInfo");
      const url = new URL(window.location.href);
      const isRegistrationPaymentReturn =
        url.searchParams.has("registrace-platba") && url.searchParams.has("id");

      if (raw) {
        let userData: UserInfo | null = null;

        try {
          userData = JSON.parse(raw) as UserInfo;
        } catch {
          await clearSession();
        }

        if (userData?.token) {
          if (isRegistrationPaymentReturn) {
            await checkPayment(userData);
          } else {
            const isOk = await verifyToken(userData);

            if (isOk) {
              clearLoginHash();
            }

            if (!isOk) {
              await clearSession();
            } else if (userInfoGlobal?.membership === "REGISTER") {
              setIsLoggedIn(false);
              setIsPendingRegistration(true);
              await checkPayment(userData);
            } else {
              await checkPayment(userData);
              setIsLoggedIn(true);
            }
          }
        } else {
          await clearSession();
          await checkPayment();
        }
      } else {
        void clearSession();
        await checkPayment();
      }

      // unsubscribe url
      /// make sure its only unsubscribe=token
      const tokenParam = url.searchParams.get("unsubscribe_token");
      if (tokenParam) {
        await unsubscribeEmail(tokenParam);
        router.push("/");
      }

      setIsInitialLoading(false);
    };

    init();
  }, []);

  return (
    <SessionContext.Provider
      value={{
        showPassword,
        setShowPassword,
        showPasswordRepeat,
        setShowPasswordRepeat,
        isLoading,
        isPendingRegistration,
        doPayment,
        setDoPayment,
        latestEstateOffers,
        handleSubmitPayment,
        selectedProduct,
        setSelectedProduct,
        registrationPromoCode,
        setRegistrationPromoCode,
        registrationPromoInfo,
        setRegistrationPromoInfo,
        isPromoCodeChecking,
        setIsPromoCodeChecking,
        handleSubmit,
        passwordMatch,
        doForgottenPassword,
        setDoForgottenPassword,
        isInitialLoading,
        isPaymentLoading,
        setIsInitialLoading,
        onLoginSuccess,
        isPaymentSuccess,
        setIsPaymentSuccess,
        loginSignal,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext = () => useContext(SessionContext);
